import { useEffect, useState } from "react";
import { Form, Col, Row } from "antd";
import useGet from "../../../global/hooks/useGet";
import usePost from "../../../global/hooks/usePost";
import usePut from "../../../global/hooks/usePut";
import useDelete from "../../../global/hooks/useDelete";
import CustomInput from "../../../global/components/inputWidgets/CustomInput";
import CustomSelect from "../../../global/components/inputWidgets/CustomSelect";
import { useNavigate, useParams } from "react-router-dom";
import {
  openConfirmNotification,
  openErrorNotification,
} from "../../../global/utils/toast";

import {
  CheckCircleOutlined,
  CloseOutlined,
  DeleteOutlined,
  EditOutlined,
  UnorderedListOutlined,
} from "@ant-design/icons";
// import CustomSwitch from "../../../global/components/inputWidgets/CustomSwitch";
import CommonBreadcrumb from "../../../global/components/CommonBreadcrumb";
import CommonButton from "../../../global/components/CommonButton";
import { getModifiedResponse } from "../../../global/utils/responseModifier";
import ConfirmModal from "../../../global/components/ConfirmModal";
import { FaAddressCard } from "react-icons/fa6";

export const LicenseCodeForm = ({ type }) => {
  const [showModal, setShowModal] = useState(false);
  const [itemValue, setItemValue] = useState(null);
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();

  const url = id
    ? `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/${id}/`
    : undefined;

  const optionUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/getoptions/`;
  const postUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`;
  const putUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/${id}/`;
  const deleteUrl = `${process.env.REACT_APP_API_URL}/api/licensecodes/codes/`;
  const { data, loading, error, refetch } = useGet(url);

  // console.log("individualLicenseData", individualLicenseData);

  const { data: optionsData, error: optionDataError } = useGet(optionUrl);

  const {
    data: postData,
    error: postError,
    postData: postAction,
  } = usePost(postUrl);
  const { data: putData, error: putError, putData: putAction } = usePut(putUrl);
  const {
    data: deletedData,
    error: deletedDataError,
    deleteMultiple,
  } = useDelete(deleteUrl);

  const modifiedItems = getModifiedResponse(optionsData, "items");
  // const modifiedTypes = getModifiedResponse(optionsData, "types");

  useEffect(() => {
    if (!id && [1, 2].includes(itemValue)) {
      form.setFieldsValue({
        Surcharge: 0,
      });
    }
  }, [id, itemValue]);

  const onFinish = async (values) => {
    try {
      if (id) {
        const modifiedValue = {
          ...values,
          Annual: values.Annual
            ? Number(parseFloat(values.Annual).toFixed(2))
            : values.Annual,
          Lifetime: values.Lifetime
            ? Number(parseFloat(values.Lifetime).toFixed(2))
            : values.Lifetime,
          Code: parseInt(values.Code),
        };
        await putAction(modifiedValue);
      } else {
        const modifiedValue = {
          ...values,
          Annual: values.Annual
            ? Number(parseFloat(values.Annual).toFixed(2))
            : values.Annual,
          Lifetime: values.Lifetime
            ? Number(parseFloat(values.Lifetime).toFixed(2))
            : values.Lifetime,
          Code: parseInt(values.Code),
        };
        await postAction(modifiedValue);
      }
    } catch (error) {
      openErrorNotification("Something went wrong.");
      console.log(error);
    }
  };

  const onReset = () => {
    navigate(-1);
  };

  const onDeleteConfirm = async () => {
    try {
      await deleteMultiple({ ids: [Number(id)] });
    } catch (error) {
      openErrorNotification("Something went wrong.");
    }
    setShowModal(false);
  };
  // console.log("data", data?.data?.Online === 0);
  useEffect(() => {
    const getLiceneCodeInfoById = () => {
      try {
        form.resetFields();
        if (data?.data) {
          form.setFieldsValue({
            Code: data?.data?.Code,
            CodeName: data?.data?.CodeName || "",
            Item: data?.data?.Item,
            Type: data?.data?.Type,
            Annual: data?.data?.Annual || "",
            Lifetime: data?.data?.Lifetime || "",
            Surcharge: data?.data?.Surcharge,
          });
          setItemValue(data?.data?.Item);
        } else {
          console.warn("No data found for the given ID");
        }
      } catch (error) {
        console.log({ error });
        openErrorNotification("Error fetching data!");
      }
    };

    getLiceneCodeInfoById();
  }, [id, data, form, type]);

  //toat for create
  useEffect(() => {
    if (Array.isArray(postError?.response?.data?.errors?.non_field_errors)) {
      postError?.response?.data?.errors?.non_field_errors?.map((item) =>
        openErrorNotification(item)
      );
      return;
    }
    if (postError) {
      openErrorNotification(
        postError?.response?.data?.message || "Something went wrong"
      );
    } else if (!postError && postData) {
      openConfirmNotification("Success");
      setTimeout(() => {
        navigate(-1);
      }, 200);
    }
  }, [postError, postData]);

  //toat for update
  useEffect(() => {
    if (putError) {
      openErrorNotification(
        putError?.response?.data?.message || "Something went wrong"
      );
    } else if (!putError && putData) {
      openConfirmNotification("Success");
      refetch();
      navigate("/licensecode");
    }
  }, [putData, putError]);

  //toast for delete
  useEffect(() => {
    if (deletedData && !deletedDataError) {
      openConfirmNotification("Success");
      // refetch();
      navigate("/licensecode");
    } else if (deletedDataError) {
      openErrorNotification(
        deletedDataError?.response?.data?.message || "Something went wrong"
      );
    } else {
      return;
    }
  }, [deletedData, deletedDataError]);

  const breadcrumbItems = [
    {
      label: "License Code",
      href: "/licensecode",
    },
    {
      label: id ? (type === "info" ? "Information" : "Edit") : "Add",
      href: id
        ? type === "info"
          ? `/licensecode/info/${id}`
          : `/licensecode/edit/${id}`
        : "/licensecode/add/",
    },
  ];

  const buttonsData =
    type === "info"
      ? [
          {
            label: "Edit",
            icon: <EditOutlined />,
            onClick: () => {
              navigate(`/licensecode/edit/${id}`);
            },
          },
          {
            label: "Delete",
            icon: <DeleteOutlined />,
            onClick: () => {
              setShowModal(true);
            },
          },
          {
            label: "List",
            icon: <UnorderedListOutlined />,
            onClick: () => {
              navigate(-1);
            },
          },
        ]
      : type === "edit"
      ? [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ]
      : [
          {
            label: "Apply",
            icon: <CheckCircleOutlined />,
            onClick: () => form.submit(),
          },
          {
            label: "Cancel",
            icon: <CloseOutlined />,
            onClick: onReset,
          },
        ];

  return (
    <div>
      <div className="pb-4 sticky -mt-4 pt-[6px] top-[55px] z-10 bg-[#f4f5f9]">
        <CommonBreadcrumb breadcrumbItems={breadcrumbItems}>
          <CommonButton buttonsData={buttonsData} />
        </CommonBreadcrumb>
      </div>

      <div className="">
        <Form
          validateTrigger={"onChange"}
          form={form}
          onFinish={onFinish}
          scrollToFirstError
          layout={"vertical"}
          autoComplete={"off"}
        >
          <div className="border bg-white rounded-[4px] overflow-hidden">
            <div className="flex items-center bg-gray-200 p-2.5">
              <FaAddressCard className="text-[18px] text-black " />
              <span className="text-[.935rem] text-black font-['Poppins'] ml-2">
                License Code
              </span>
            </div>
            <div className="px-2 pt-1 pb-3">
              <Row gutter={[16, 8]}>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Name"
                    name="CodeName"
                    disabled={type === "info"}
                    autoComplete="off"
                    required
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Item"
                    name="Item"
                    options={modifiedItems}
                    placeholder="Please select"
                    disabled={type === "info"}
                    required
                    loading={loading}
                    onChange={(value) => {
                      setItemValue(value);
                      if ([1, 2].includes(value)) {
                        form.setFieldsValue({
                          Surcharge: 0,
                        });
                      }
                    }}
                  />
                </Col>
                {/* <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomSelect
                    label="Type"
                    name="Type"
                    options={modifiedTypes}
                    placeholder="Please select"
                    disabled={type === "info"}
                    required
                  />
                </Col> */}
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Code"
                    name="Code"
                    disabled={type === "info"}
                    type="number"
                    loading={loading}
                    required
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Annual"
                    name="Annual"
                    disabled={type === "info"}
                    type="number"
                    required
                    prefix={"$"}
                    style={{ height: "30px" }}
                    loading={loading}
                  />
                </Col>
                <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                  <CustomInput
                    label="Lifetime"
                    name="Lifetime"
                    disabled={type === "info"}
                    type="number"
                    required
                    prefix={"$"}
                    style={{ height: "30px" }}
                    loading={loading}
                  />
                </Col>
                {/* {[1, 2].includes(itemValue) && (
                  <Col xs={24} sm={24} md={12} lg={12} xl={12}>
                    <CustomSelect
                      label="Surcharge"
                      name="Surcharge"
                      options={discountOptions}
                      placeholder="Please select"
                      disabled={type === "info"}
                      loading={loading}
                    />
                  </Col>
                )} */}
              </Row>
            </div>
          </div>
        </Form>
      </div>
      <ConfirmModal
        showModal={showModal}
        setShowModal={setShowModal}
        onDeleteConfirm={onDeleteConfirm}
      />
    </div>
  );
};
